import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Crosshair, Minus, Plus, Scan } from 'lucide-react'
import MapTileLayer from '@/components/strategy/MapTileLayer'
import type { MapInfo } from '@/lib/maps'
import type { Weapon } from '@/lib/artillery'
import {
  boundsCenter, clampToBounds, distanceMeters, formatGameCoord, toGame, toWorld, worldSize,
  type GameCalibration, type GamePoint,
} from '@/lib/gameMap'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Mortar hesap makinesinin harita yüzeyi.
 *
 * Strateji editörünün tile altyapısını (`MapTileLayer`) olduğu gibi kullanır ama
 * Fabric'i hiç kurmaz: burada çizilecek bir tuval yok, iki nokta var. Bu yüzden
 * pan/zoom doğrudan bir viewport transform state'inde tutulur (`vpt`) ve
 * üstündeki her şey — ızgara, menzil çemberleri, atış hattı — tek bir `<g
 * transform>` altında **dünya koordinatlarında** çizilir. Dünya birimi metre
 * olduğu için menzil çemberinin yarıçapı doğrudan silahın menzilidir; bkz.
 * `lib/gameMap.ts`.
 *
 * Marker'lar SVG'de değil DOM'da: sürüklenmeleri, imleç değiştirmeleri ve
 * etiketleri var, yani hepsi zoom'la ölçeklenmemesi gereken şeyler.
 */

/** Ölçek tavanı, px/m. Piramit 2 px/m'de bittiği için üstü büyütmedir —
 *  yine de nokta koymayı hassaslaştırdığı için bir miktar pay bırakılır. */
const MAX_SCALE = 3

/** Taban, "tüm oynanabilir alan ekrana sığar" ölçeğinin çarpanı. */
const MIN_ZOOM_FIT_FACTOR = 0.9

const ZOOM_BUTTON_FACTOR = 1.5
const FIT_PADDING = 26

/**
 * Ölü bölgenin ve menzil dışı atış hattının rengi.
 *
 * Temanın `--primary`'si de kırmızı, o yüzden burada daha açık ve daha soğuk
 * bir kırmızı kullanılıyor: "menzil sınırı" ile "buraya atamazsın" aynı renk
 * olursa iki bilgi tek bilgiye iner.
 */
const DEAD_ZONE_COLOR = '#ff6b6b'

/**
 * Menzil dışındaki atış hattının rengi — **amber, kırmızı değil.**
 *
 * Tema kırmızı: menzil sınırı `--primary`, ölü bölge açık kırmızı. Hattı da
 * kırmızıya çevirmek sinyali yok ediyordu, çünkü ekranda zaten iki kırmızı
 * çember var ve üçüncü bir kırmızı "sorun var" demiyor, sadece kalabalık
 * yapıyor. Amber bu ekranda başka hiçbir yerde geçmez, dolayısıyla değiştiği
 * an gözle yakalanır.
 */
const OUT_OF_RANGE_COLOR = '#ffc24d'

/**
 * Menzil dışını karartan maske: tüm dünya dikdörtgeni eksi azami menzil
 * çemberi (`fill-rule: evenodd` ile delik).
 *
 * Atış alanını **boyamak yerine dışını söndürüyoruz.** Bandı kırmızıya
 * boyamak haritayı okunmaz hâle getiriyordu: zemin zaten renkli, üstüne
 * geniş bir ton binince hem arazi hem menzil çizgisi kayboluyor. Söndürme
 * ters yönden çalışır — vurulabilen alan haritanın *aydınlık kalan* yeri
 * olur, o yüzden sınır renk değil parlaklık farkıyla okunur.
 */
function outsideRangeMask(cx: number, cy: number, r: number, world: number): string {
  return `M 0 0 H ${world} V ${world} H 0 Z ${circlePath(cx, cy, r)}`
}

/** Tek çemberi path olarak verir — maskelerde delik olarak kullanılır. */
function circlePath(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0 Z`
}

/** Izgara: küçük adım 1 birim (100 m), büyük adım 10 birim (1 km). */
const MINOR_STEP = 1
const MAJOR_STEP = 10
/** Küçük ızgara ancak çizgiler bu kadar px aralıklıysa çizilir. */
const MINOR_MIN_PX = 9

type Which = 'battery' | 'target'

interface Props {
  map: MapInfo
  cal: GameCalibration
  battery: GamePoint
  target: GamePoint
  weapon: Weapon
  /** Boş haritaya tıklamak hangi noktayı taşır. */
  place: Which
  onMove: (which: Which, p: GamePoint) => void
  /** Zemine uygulanan CSS filtresi (taktiksel görünüm). */
  filter?: string
}

interface Vpt { a: number; e: number; f: number }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export default function CalculatorMap({
  map, cal, battery, target, weapon, place, onMove, filter,
}: Props) {
  const { T } = useLanguage()
  const hostRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  const ws = useMemo(() => worldSize(cal), [cal])

  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setSize(s => (s.w === r.width && s.h === r.height ? s : { w: r.width, h: r.height }))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const [vpt, setVpt] = useState<Vpt>({ a: 0, e: 0, f: 0 })

  /** "Tüm oynanabilir alan sığar" ölçeği — zoom tabanı buradan türer. */
  const fitScale = useMemo(() => {
    if (!size.w || !size.h) return 0
    const w = (cal.bounds.maxX - cal.bounds.minX) * cal.metersPerUnit
    const h = (cal.bounds.maxY - cal.bounds.minY) * cal.metersPerUnit
    return Math.min(
      Math.max(1, size.w - FIT_PADDING * 2) / w,
      Math.max(1, size.h - FIT_PADDING * 2) / h,
    )
  }, [cal, size.w, size.h])

  /** Verilen noktayı verilen ölçekte ortalayan transform. */
  const centered = useCallback((a: number, at: GamePoint): Vpt => {
    const c = toWorld(at, cal)
    return { a, e: size.w / 2 - c.x * a, f: size.h / 2 - c.y * a }
  }, [cal, size.w, size.h])

  /**
   * Açılış kadrajı: **tüm harita değil, bataryanın menzili**.
   *
   * Oynanabilir alan 11 km genişliğinde, havanın menzili 684 m — haritanın
   * tamamına sığdırılmış bir açılış görüntüsünde menzil çemberi kırk piksel
   * kalır ve iki nokta üst üste biner. Hesap makinesini açan kişinin ilk
   * baktığı şey bataryasının çevresi, o yüzden kadraj oraya kurulur; bütün
   * haritaya çıkmak bir düğme uzakta (`fit`).
   */
  const rangeScale = useMemo(() => {
    if (!size.w || !size.h) return 0
    const span = weapon.maxRangeM * 2 * 1.55   // çember ekranı tam doldurmasın
    return Math.min(Math.min(size.w, size.h) / span, MAX_SCALE)
  }, [weapon.maxRangeM, size.w, size.h])

  /**
   * Kadrajı toparlar: ölçeği sınırlar ve viewport'un ortasının dünyanın dışına
   * kaçmasına izin vermez — haritayı ekrandan tamamen kaydırıp kaybetmek
   * mümkün olmasın.
   */
  const settle = useCallback((v: Vpt): Vpt => {
    const a = clamp(v.a, (fitScale || v.a) * MIN_ZOOM_FIT_FACTOR, MAX_SCALE)
    const k = v.a > 0 ? a / v.a : 1
    // Ölçek kırpıldıysa merkezi koru: kırpma, farkı offset'e yansıtmadan
    // uygulanırsa görüntü kenara kayar.
    let e = size.w / 2 - (size.w / 2 - v.e) * k
    let f = size.h / 2 - (size.h / 2 - v.f) * k
    const cx = clamp((size.w / 2 - e) / a, 0, ws)
    const cy = clamp((size.h / 2 - f) / a, 0, ws)
    e = size.w / 2 - cx * a
    f = size.h / 2 - cy * a
    return { a, e, f }
  }, [fitScale, size.w, size.h, ws])

  // Harita değişince kadraj yeniden kurulur; aynı harita üzerinde pencere
  // boyutu değişirse zoom korunur, yalnızca sınırlar tazelenir.
  //
  // `battery` bilerek bağımlılıkta değil: batarya her sürükleme karesinde
  // değişiyor, kadraj ise yalnızca haritaya ilk girişte kurulur.
  const framedFor = useRef('')
  const batteryRef = useRef(battery)
  batteryRef.current = battery
  useEffect(() => {
    if (!size.w || !size.h || !fitScale) return
    if (framedFor.current !== map.name) {
      framedFor.current = map.name
      setVpt(centered(rangeScale || fitScale, batteryRef.current))
      return
    }
    setVpt(v => (v.a > 0 ? settle(v) : centered(rangeScale || fitScale, batteryRef.current)))
  }, [map.name, fitScale, rangeScale, size.w, size.h, centered, settle])

  /** Tüm oynanabilir alanı ekrana sığdırır. */
  const fit = useCallback(
    () => setVpt(centered(fitScale, boundsCenter(cal))),
    [cal, centered, fitScale],
  )

  /** Ekran noktasını (host'a göre) oyun koordinatına çevirir. */
  const pointAt = useCallback((clientX: number, clientY: number): GamePoint | null => {
    const el = hostRef.current
    if (!el || !(vpt.a > 0)) return null
    const r = el.getBoundingClientRect()
    return clampToBounds(
      toGame({ x: (clientX - r.left - vpt.e) / vpt.a, y: (clientY - r.top - vpt.f) / vpt.a }, cal),
      cal,
    )
  }, [cal, vpt])

  /** Oyun koordinatının ekran (host içi) konumu. */
  const screenOf = useCallback((p: GamePoint) => {
    const w = toWorld(p, cal)
    return { x: w.x * vpt.a + vpt.e, y: w.y * vpt.a + vpt.f }
  }, [cal, vpt])

  // ── Pan / zoom / pinch ────────────────────────────────────────────────────
  //
  // Tek işaretçi sürükler, iki işaretçi yakınlaştırır. İşaretçiler bir Map'te
  // tutulur çünkü dokunmatikte ikincisi sürükleme başladıktan sonra iner.
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef<{ moved: boolean; dist: number; mid: { x: number; y: number } } | null>(null)
  /** Marker sürükleniyorsa hangisi — zemin yerine o taşınır. */
  const dragging = useRef<Which | null>(null)

  const zoomAt = useCallback((factor: number, cx: number, cy: number) => {
    setVpt(v => {
      if (!(v.a > 0)) return v
      const a = clamp(v.a * factor, (fitScale || v.a) * MIN_ZOOM_FIT_FACTOR, MAX_SCALE)
      const k = a / v.a
      return settle({ a, e: cx - (cx - v.e) * k, f: cy - (cy - v.f) * k })
    })
  }, [fitScale, settle])

  /**
   * Tekerlek elle bağlanır, JSX'teki `onWheel` ile değil: React kök seviyesinde
   * wheel dinleyicisini **passive** kurar, yani oradaki `preventDefault()` hiçbir
   * şey yapmaz — harita yakınlaşırken sayfa da kayardı.
   */
  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault()
      const r = el.getBoundingClientRect()
      zoomAt(ev.deltaY < 0 ? 1.15 : 1 / 1.15, ev.clientX - r.left, ev.clientY - r.top)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomAt])

  const onPointerDown = (ev: React.PointerEvent) => {
    ;(ev.target as Element).setPointerCapture?.(ev.pointerId)
    pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    gesture.current = { moved: false, dist: 0, mid: { x: 0, y: 0 } }
  }

  const onPointerMove = (ev: React.PointerEvent) => {
    const previous = pointers.current.get(ev.pointerId)
    if (!previous) return
    pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })

    const points = [...pointers.current.values()]

    // Sürüklenen marker varsa jest onun; harita sabit kalır.
    if (dragging.current) {
      const p = pointAt(ev.clientX, ev.clientY)
      if (p) onMove(dragging.current, p)
      if (gesture.current) gesture.current.moved = true
      return
    }

    if (points.length >= 2) {
      const [p1, p2] = points
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y)
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
      const g = gesture.current
      if (g) {
        if (g.dist > 0 && dist > 0) {
          const el = hostRef.current
          if (el) {
            const r = el.getBoundingClientRect()
            zoomAt(dist / g.dist, mid.x - r.left, mid.y - r.top)
          }
        }
        g.dist = dist
        g.mid = mid
        g.moved = true
      }
      return
    }

    const dx = ev.clientX - previous.x
    const dy = ev.clientY - previous.y
    if (Math.abs(dx) + Math.abs(dy) > 0 && gesture.current) gesture.current.moved = true
    setVpt(v => settle({ ...v, e: v.e + dx, f: v.f + dy }))
  }

  const onPointerUp = (ev: React.PointerEvent) => {
    const wasDragging = dragging.current
    const moved = gesture.current?.moved ?? false
    pointers.current.delete(ev.pointerId)
    if (pointers.current.size === 0) gesture.current = null
    dragging.current = null

    // Sürüklemeden bırakmak "buraya koy" demektir; marker'ın kendisine tıklamak
    // onu yerinde bırakır.
    if (!wasDragging && !moved && pointers.current.size === 0) {
      const p = pointAt(ev.clientX, ev.clientY)
      if (p) onMove(place, p)
    }
  }

  // ── İmleç okuması ─────────────────────────────────────────────────────────
  const [hover, setHover] = useState<GamePoint | null>(null)

  // ── Izgara ────────────────────────────────────────────────────────────────
  //
  // Görünür dünya dikdörtgeni oyun koordinatına çevrilir ve yalnızca o aralıktaki
  // çizgiler basılır: tüm haritayı taramak 160 çizgi/eksen demek, ekranda olan
  // ise bir düzine.
  const grid = useMemo(() => {
    if (!(vpt.a > 0) || !size.w || !size.h) {
      return { xs: [] as number[], ys: [] as number[], minor: false }
    }
    const topLeft = toGame({ x: -vpt.e / vpt.a, y: -vpt.f / vpt.a }, cal)
    const bottomRight = toGame(
      { x: (size.w - vpt.e) / vpt.a, y: (size.h - vpt.f) / vpt.a },
      cal,
    )
    const minor = vpt.a * cal.metersPerUnit * MINOR_STEP >= MINOR_MIN_PX
    const step = minor ? MINOR_STEP : MAJOR_STEP

    const ticks = (from: number, to: number) => {
      const lo = Math.max(Math.min(from, to), 0)
      const hi = Math.min(Math.max(from, to), cal.tileBounds.maxY)
      const out: number[] = []
      for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) out.push(v)
      return out
    }

    return {
      xs: ticks(topLeft.x, bottomRight.x),
      ys: ticks(bottomRight.y, topLeft.y),
      minor,
    }
  }, [cal, vpt, size.w, size.h])

  const isMajor = (v: number) => Math.abs(v / MAJOR_STEP - Math.round(v / MAJOR_STEP)) < 1e-8

  // ── Çizim geometrisi ──────────────────────────────────────────────────────
  const batteryWorld = toWorld(battery, cal)
  const targetWorld = toWorld(target, cal)
  const batteryScreen = screenOf(battery)
  const targetScreen = screenOf(target)

  // Hedef menzilde mi — atış hattının rengi buna bakar. Panel aynı hesabı
  // kendi yapar; ikisi de `lib/artillery`'nin menzil sınırlarını okuyor.
  const distance = distanceMeters(battery, target, cal)
  const inRange =
    distance + 1e-6 >= weapon.minRangeM && distance <= weapon.maxRangeM + 1e-6

  const boundsWorld = {
    x: (cal.bounds.minX - cal.tileBounds.minX) * cal.metersPerUnit,
    y: (cal.tileBounds.maxY - cal.bounds.maxY) * cal.metersPerUnit,
    w: (cal.bounds.maxX - cal.bounds.minX) * cal.metersPerUnit,
    h: (cal.bounds.maxY - cal.bounds.minY) * cal.metersPerUnit,
  }

  /**
   * Nokta işaretçisi. Etiketler ters yönlere yazılır (batarya aşağı, hedef
   * yukarı): iki nokta birbirine yakınken aynı tarafa yazılan etiketler üst
   * üste biniyordu, ve kısa mesafe bu araçta istisna değil kuraldır.
   */
  const marker = (which: Which, p: { x: number; y: number }, label: string) => (
    <div
      key={which}
      role="button"
      tabIndex={-1}
      aria-label={label}
      className="absolute z-10 touch-none"
      style={{ left: p.x, top: p.y, transform: 'translate(-50%, -50%)' }}
      onPointerDown={ev => {
        // Zemin jestine dönüşmesin: bu işaretçi marker'ı taşır.
        dragging.current = which
        onPointerDown(ev)
        ev.stopPropagation()
      }}
    >
      <div
        className={`flex h-6 w-6 cursor-grab items-center justify-center rounded-full border-2 text-[10px] font-bold shadow-lg shadow-black/50 active:cursor-grabbing ${
          which === 'battery'
            ? 'border-primary bg-primary/25 text-primary'
            : 'border-[#e8e8ea] bg-[#e8e8ea]/20 text-[#e8e8ea]'
        }`}
      >
        {which === 'battery' ? '◎' : '✕'}
      </div>
      <span
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-black/70 px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-wider backdrop-blur-sm ${
          which === 'battery'
            ? 'top-full mt-1 text-primary'
            : 'bottom-full mb-1 text-[#e8e8ea]'
        }`}
      >
        {label}
      </span>
    </div>
  )

  const btnClass =
    'flex h-8 w-8 items-center justify-center rounded-sm border border-white/12 bg-black/60 ' +
    'text-foreground/80 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-foreground ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <div
      ref={hostRef}
      className="relative h-full w-full cursor-crosshair touch-none overflow-hidden bg-[#0c0c0f]"
      onPointerDown={onPointerDown}
      onPointerMove={ev => {
        onPointerMove(ev)
        setHover(pointAt(ev.clientX, ev.clientY))
      }}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={() => setHover(null)}
      onContextMenu={ev => ev.preventDefault()}
    >
      <MapTileLayer
        tiles={map.tiles}
        base={map.base}
        vpt={vpt}
        worldSize={ws}
        filter={filter}
      />

      <svg className="pointer-events-none absolute inset-0 h-full w-full">
        <defs>
          {/* Ölü bölgenin dolgusu: merkezde neredeyse şeffaf, kenara doğru
              koyulaşır. Düz 0.72'lik dolgu bataryanın çevresini karartıp
              konum seçmeyi zorlaştırıyordu; imkânsızlığı sınırdaki koyuluk ve
              kesik çizgi anlatır, içerisi okunur kalır. `objectBoundingBox`
              olduğu için çemberle birlikte ölçeklenir. */}
          <radialGradient id="calc-dead-zone" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#05050a" stopOpacity={0.05} />
            <stop offset="55%" stopColor="#05050a" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#05050a" stopOpacity={0.62} />
          </radialGradient>
        </defs>
        <g transform={`translate(${vpt.e} ${vpt.f}) scale(${vpt.a})`}>
          {/* Izgara. `non-scaling-stroke` sayesinde çizgi kalınlığı ve kesik
              deseni zoom'dan bağımsız kalır; aksi halde yakınlaştırırken
              çizgiler parmak kalınlığına çıkardı. */}
          {grid.xs.map(x => {
            const wx = (x - cal.tileBounds.minX) * cal.metersPerUnit
            return (
              <line
                key={`x${x}`}
                x1={wx} y1={0} x2={wx} y2={ws}
                stroke={isMajor(x) ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.09)'}
                strokeWidth={isMajor(x) ? 1 : 0.75}
                vectorEffect="non-scaling-stroke"
              />
            )
          })}
          {grid.ys.map(y => {
            const wy = (cal.tileBounds.maxY - y) * cal.metersPerUnit
            return (
              <line
                key={`y${y}`}
                x1={0} y1={wy} x2={ws} y2={wy}
                stroke={isMajor(y) ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.09)'}
                strokeWidth={isMajor(y) ? 1 : 0.75}
                vectorEffect="non-scaling-stroke"
              />
            )
          })}

          {/* Oynanabilir alan — dışına nokta konamaz, o yüzden çizilir. */}
          <rect
            x={boundsWorld.x} y={boundsWorld.y}
            width={boundsWorld.w} height={boundsWorld.h}
            fill="none"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth={1}
            strokeDasharray="6 5"
            vectorEffect="non-scaling-stroke"
          />

          {/* ── Menzil ──────────────────────────────────────────────────────
              Üç bölge, üç ayrı iş — ve hepsi **parlaklıkla** anlatılıyor,
              renkle değil. Zemin zaten renkli bir uydu görüntüsü; üstüne
              geniş renk tonları binince hem arazi hem menzil çizgisi
              kayboluyor.

              1. **Menzil dışı** — söndürülür. Böylece vurulabilen alan
                 haritanın aydınlık kalan yeri olur; sınır, çizgiye bakmaya
                 gerek kalmadan görünür. Karartma ızgaranın üstüne biner,
                 yani ızgara da bandın içinde daha net durur. Bandın kendisine
                 **hiç ton verilmez**: 0.04'lük bir kırmızı bile bu genişlikte
                 zemini turuncuya çeviriyor ve araziyi okunmaz yapıyordu.
              2. **Ölü bölge** (asgari menzilin içi): kenarı koyu, merkezi
                 açık bir gradyan. Orası bir uyarı değil, bir imkânsızlık
                 (havan o kadar yakına düşmez), ama içi düz karartılınca
                 bataryanın yeri seçilemiyordu.
              3. **Sınır çizgileri ve atış hattı** — her biri iki kez çizilir:
                 altta siyah bir halo, üstünde renk. Parlak tarlanın üstünde
                 tek kat ince çizgi kayboluyordu. (`paint-order` yerine iki
                 eleman: kesik desenin altına da halo gerekiyor.)

              Renkler `style` ile veriliyor, öznitelikle değil: temanın rengi bir
              CSS değişkeni (`--primary`) ve `var()` sunum özniteliklerinde
              tarayıcıya göre çözülür — inline stilde her yerde çözülür. */}
          <path
            d={outsideRangeMask(batteryWorld.x, batteryWorld.y, weapon.maxRangeM, ws)}
            fillRule="evenodd"
            fill="#05050a"
            fillOpacity={0.42}
          />
          {weapon.minRangeM > 0 && (
            <circle
              cx={batteryWorld.x} cy={batteryWorld.y} r={weapon.minRangeM}
              fill="url(#calc-dead-zone)"
            />
          )}

          {/* Azami menzil sınırı */}
          <circle
            cx={batteryWorld.x} cy={batteryWorld.y} r={weapon.maxRangeM}
            fill="none"
            stroke="rgba(0,0,0,0.65)"
            strokeWidth={5}
            strokeDasharray="7 5"
            vectorEffect="non-scaling-stroke"
          />
          <circle
            cx={batteryWorld.x} cy={batteryWorld.y} r={weapon.maxRangeM}
            fill="none"
            style={{ stroke: 'var(--primary)' }}
            strokeWidth={2.25}
            strokeDasharray="7 5"
            vectorEffect="non-scaling-stroke"
          />

          {/* Asgari menzil (ölü bölgenin sınırı) */}
          {weapon.minRangeM > 0 && (
            <>
              <circle
                cx={batteryWorld.x} cy={batteryWorld.y} r={weapon.minRangeM}
                fill="none"
                stroke="rgba(0,0,0,0.65)"
                strokeWidth={4.5}
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={batteryWorld.x} cy={batteryWorld.y} r={weapon.minRangeM}
                fill="none"
                stroke={DEAD_ZONE_COLOR}
                strokeWidth={2}
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}

          {/* Atış hattı. Menzil dışındayken amber: sayıyı okumadan önce
              çözümün olmadığı haritadan anlaşılsın. */}
          <line
            x1={batteryWorld.x} y1={batteryWorld.y}
            x2={targetWorld.x} y2={targetWorld.y}
            stroke="rgba(0,0,0,0.6)"
            strokeWidth={5}
            strokeDasharray="8 6"
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={batteryWorld.x} y1={batteryWorld.y}
            x2={targetWorld.x} y2={targetWorld.y}
            stroke={inRange ? undefined : OUT_OF_RANGE_COLOR}
            style={inRange ? { stroke: 'var(--primary)' } : undefined}
            strokeWidth={2}
            strokeDasharray="8 6"
            vectorEffect="non-scaling-stroke"
          />
        </g>

        {/* Izgara etiketleri ekran uzayında durur: kenarda sabit bir cetvel
            gibi okunur, haritayla birlikte kayıp gitmez. */}
        <g className="font-mono" fill="rgba(255,255,255,0.45)" fontSize={9}>
          {grid.xs.filter(isMajor).map(x => (
            <text
              key={`lx${x}`}
              x={(x - cal.tileBounds.minX) * cal.metersPerUnit * vpt.a + vpt.e + 3}
              y={11}
            >
              {formatGameCoord(x)}
            </text>
          ))}
          {grid.ys.filter(isMajor).map(y => (
            <text
              key={`ly${y}`}
              x={3}
              y={(cal.tileBounds.maxY - y) * cal.metersPerUnit * vpt.a + vpt.f - 3}
            >
              {formatGameCoord(y)}
            </text>
          ))}
        </g>
      </svg>

      {marker('battery', batteryScreen, T.calculator.battery)}
      {marker('target', targetScreen, T.calculator.target)}

      {/* Zoom denetimleri ve kadrajı toparlama. */}
      <div className="absolute right-3 top-3 z-20 flex flex-col gap-1.5">
        <button
          type="button"
          className={btnClass}
          aria-label={T.calculator.zoomIn}
          onClick={() => zoomAt(ZOOM_BUTTON_FACTOR, size.w / 2, size.h / 2)}
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          className={btnClass}
          aria-label={T.calculator.zoomOut}
          onClick={() => zoomAt(1 / ZOOM_BUTTON_FACTOR, size.w / 2, size.h / 2)}
        >
          <Minus className="h-4 w-4" />
        </button>
        <button type="button" className={btnClass} aria-label={T.calculator.fitMap} onClick={fit}>
          <Scan className="h-4 w-4" />
        </button>
      </div>

      {/* Küçük bir anahtar: iki çember iki farklı şey söylüyor ve renk tek
          başına bunu anlatmıyor. Yalnızca silahın asgari menzili varsa ölü
          bölge satırı basılır. */}
      <div className="pointer-events-none absolute bottom-3 right-3 z-20 flex flex-col items-end gap-1">
        <span className="flex items-center gap-1.5 rounded-sm border border-white/10 bg-black/65 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-wider text-foreground/75 backdrop-blur-sm">
          <span className="h-0 w-4 border-t-2 border-dashed" style={{ borderColor: 'var(--primary)' }} />
          {T.calculator.legendRange}
        </span>
        {weapon.minRangeM > 0 && (
          <span className="flex items-center gap-1.5 rounded-sm border border-white/10 bg-black/65 px-2 py-1 font-display text-[9px] font-bold uppercase tracking-wider text-foreground/75 backdrop-blur-sm">
            <span
              className="h-3 w-3 rounded-full border-2 border-dashed bg-black/80"
              style={{ borderColor: DEAD_ZONE_COLOR }}
            />
            {T.calculator.legendDead}
          </span>
        )}
      </div>

      {/* İmlecin altındaki koordinat — haritadan okuma yaparken gereken şey. */}
      <div className="pointer-events-none absolute bottom-3 left-3 z-20 flex items-center gap-1.5 rounded-sm border border-white/10 bg-black/65 px-2 py-1 font-mono text-[10px] text-foreground/75 backdrop-blur-sm">
        <Crosshair className="h-3 w-3 text-primary" />
        {hover
          ? `x${formatGameCoord(hover.x)}  y${formatGameCoord(hover.y)}`
          : T.calculator.hoverHint}
      </div>
    </div>
  )
}
